import Theme from './Theme';
import ThemeService from '@/frontend/services/api/themes';

const BATCH_SIZE = 50;

export default class ThemesManager {
  themes = [];

  themesById = new Map();

  pending = new Map();

  missing = new Set();

  getThemeById(id) {
    const themeId = Number(id);

    if (!Number.isInteger(themeId) || themeId <= 0) {
      return Promise.resolve(undefined);
    }

    const theme = this.getByIdSync(themeId);

    if (theme || this.missing.has(themeId)) {
      return Promise.resolve(theme);
    }

    this.load([themeId]);

    return this.pending.get(themeId)?.promise || Promise.resolve(this.getByIdSync(themeId));
  }

  getByIdSync(id) {
    return this.themesById.get(Number(id));
  }

  onLoad({ data }) {
    const id = Number(data?.id);

    if (!Number.isInteger(id) || id <= 0) {
      return;
    }

    const theme = new Theme({ ...data, id });
    const index = this.themes.findIndex(item => item.id === id);

    if (index === -1) {
      this.themes.push(theme);
    } else {
      this.themes[index] = theme;
    }

    this.themesById.set(id, theme);
    this.missing.delete(id);
    this.settle(id, theme);
  }

  settle(id, theme) {
    this.pending.get(id)?.resolve(theme);
    this.pending.delete(id);
  }

  load(themeIds = []) {
    const ids = [...new Set(themeIds.map(Number))].filter(id =>
      Number.isInteger(id)
      && id > 0
      && !this.themesById.has(id)
      && !this.pending.has(id)
      && !this.missing.has(id)
    );

    ids.forEach(id => {
      let resolve;
      const promise = new Promise(done => {
        resolve = done;
      });

      this.pending.set(id, { promise, resolve });
    });

    for (let offset = 0; offset < ids.length; offset += BATCH_SIZE) {
      const batch = ids.slice(offset, offset + BATCH_SIZE);

      ThemeService.getMultiple(batch)
        .then(({ data }) => {
          const returnedIds = new Set(batch);

          (data || []).forEach(theme => {
            if (returnedIds.has(Number(theme?.id))) {
              this.onLoad({ data: theme });
            }
          });

          // Deleted or unavailable themes must also release the layout's loading wait.
          batch.forEach(id => {
            if (this.pending.has(id)) {
              this.missing.add(id);
              this.settle(id, undefined);
            }
          });
        })
        .catch(error => {
          console.error(error);
          batch.forEach(id => this.settle(id, undefined));
        });
    }

    return this.waitAll();
  }

  waitAll() {
    return Promise.all([...this.pending.values()].map(({ promise }) => promise));
  }
}
